/**
 * Reference-counted page scroll lock for modals / drawers.
 * Works on mobile (iOS position:fixed) and desktop (wheel / overflow / scrollbar).
 */

let locks = 0
let savedScrollY = 0
let savedBodyOverflow = ''
let savedHtmlOverflow = ''
let savedBodyPosition = ''
let savedBodyTop = ''
let savedBodyWidth = ''
let savedBodyLeft = ''
let savedBodyRight = ''
let savedBodyPaddingRight = ''
let savedHtmlPaddingRight = ''
let listenersBound = false

function scrollbarWidth(): number {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth)
}

function isElement(n: EventTarget | null): n is Element {
  return n instanceof Element
}

/**
 * Regions allowed to scroll while page is locked:
 * - modal / dialog panels (not the dimmed mask chrome)
 * - portaled FilterSelect menus
 */
function allowedScrollRoot(target: Element): Element | null {
  return (
    target.closest('.filter-menu-portal') ||
    target.closest('.modal') ||
    target.closest('.dialog-panel') ||
    // Activity lottery / wuxing result overlays (not .modal)
    target.closest('.draw-panel') ||
    // Mobile drawer: page is scroll-locked while open; nav must still scroll
    target.closest('.sidebar .nav') ||
    target.closest('aside.sidebar nav.nav') ||
    null
  )
}

/** True if event is inside a region that should still scroll (modal body, dialog, etc.) */
function canScrollInside(target: EventTarget | null, deltaY: number): boolean {
  if (!isElement(target)) return false

  const root = allowedScrollRoot(target)
  if (!root) return false

  // Walk up from event target to the allowed root; allow if any ancestor is scrollable
  let node: Element | null = target
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node)
    const oy = style.overflowY
    const ox = style.overflowX
    const yScrollable =
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    const xScrollable =
      (ox === 'auto' || ox === 'scroll' || ox === 'overlay') &&
      node.scrollWidth > node.clientWidth + 1

    if (yScrollable || xScrollable) {
      if (yScrollable && deltaY !== 0) {
        const { scrollTop, scrollHeight, clientHeight } = node
        const atTop = scrollTop <= 0
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1
        // Allow only when the scrollable region can still move
        if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return true
        // At edge: block so page behind never receives chain-scroll
        return false
      }
      return true
    }
    if (node === root) break
    node = node.parentElement
  }
  return false
}

function onWheel(e: WheelEvent) {
  if (locks <= 0) return
  if (canScrollInside(e.target, e.deltaY)) return
  e.preventDefault()
}

function onTouchMove(e: TouchEvent) {
  if (locks <= 0) return
  // touchmove has no deltaY; allow only when inside a scrollable panel
  if (canScrollInside(e.target, 1) || canScrollInside(e.target, -1)) return
  e.preventDefault()
}

function onKeyDown(e: KeyboardEvent) {
  if (locks <= 0) return
  const keys = new Set([
    ' ',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown',
  ])
  if (!keys.has(e.key)) return
  const t = e.target
  if (
    t instanceof HTMLInputElement ||
    t instanceof HTMLTextAreaElement ||
    t instanceof HTMLSelectElement ||
    (t instanceof HTMLElement && t.isContentEditable)
  ) {
    return
  }
  if (canScrollInside(t, e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home' ? -1 : 1)) {
    return
  }
  e.preventDefault()
}

function bindListeners() {
  if (listenersBound || typeof document === 'undefined') return
  // capture + passive:false so preventDefault works on wheel
  document.addEventListener('wheel', onWheel, { capture: true, passive: false })
  document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false })
  document.addEventListener('keydown', onKeyDown, { capture: true })
  listenersBound = true
}

function unbindListeners() {
  if (!listenersBound || typeof document === 'undefined') return
  document.removeEventListener('wheel', onWheel, true)
  document.removeEventListener('touchmove', onTouchMove, true)
  document.removeEventListener('keydown', onKeyDown, true)
  listenersBound = false
}

export function lockBodyScroll(): void {
  if (typeof document === 'undefined') return
  locks += 1
  if (locks > 1) return

  const { body } = document
  const html = document.documentElement
  savedScrollY = window.scrollY || window.pageYOffset || 0
  savedBodyOverflow = body.style.overflow
  savedHtmlOverflow = html.style.overflow
  savedBodyPosition = body.style.position
  savedBodyTop = body.style.top
  savedBodyWidth = body.style.width
  savedBodyLeft = body.style.left
  savedBodyRight = body.style.right
  savedBodyPaddingRight = body.style.paddingRight
  savedHtmlPaddingRight = html.style.paddingRight

  const gap = scrollbarWidth()
  html.style.overflow = 'hidden'
  body.style.overflow = 'hidden'
  body.style.position = 'fixed'
  body.style.top = `-${savedScrollY}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
  if (gap > 0) {
    // Keep layout from shifting when scrollbar disappears (PC)
    body.style.paddingRight = `${gap}px`
    html.style.paddingRight = `${gap}px`
  }
  body.classList.add('scroll-locked')
  html.classList.add('scroll-locked')
  bindListeners()
}

export function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return
  if (locks === 0) return
  locks -= 1
  if (locks > 0) return

  const { body } = document
  const html = document.documentElement
  unbindListeners()
  html.style.overflow = savedHtmlOverflow
  html.style.paddingRight = savedHtmlPaddingRight
  body.style.overflow = savedBodyOverflow
  body.style.position = savedBodyPosition
  body.style.top = savedBodyTop
  body.style.left = savedBodyLeft
  body.style.right = savedBodyRight
  body.style.width = savedBodyWidth
  body.style.paddingRight = savedBodyPaddingRight
  body.classList.remove('scroll-locked')
  html.classList.remove('scroll-locked')
  window.scrollTo(0, savedScrollY)
}

/** Force clear (e.g. hard navigation). Prefer unlockBodyScroll in normal flow. */
export function resetBodyScrollLock(): void {
  if (typeof document === 'undefined') return
  locks = 1
  unlockBodyScroll()
  locks = 0
}
